import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "wouter";
import { PerformanceCagrTooltip } from "./PerformanceCagrTooltip";

import { twoDecPriceFormat } from "../../../../common/src/utils/FormatFunctions";
import { Party, PartyAccountType, InstrumentModelTypeEnum } from "../../types.generated";
import { usePrevious } from "../../../../components/src/common/Utils";
import { PerformanceGraphsTables } from "./PerformanceGraphsTables";
import { AttributionGraph, AttributionSelectorEnum, PlotTypeEnum } from "./Attribution";
import { useQueryState } from "../../../../components/src/use-query-state";
import { PerformancePeriodSelector } from "./PerformancePeriodSelector";
import { TableGrouper } from "../../../../components/src/react-table/TableGrouper";
import { firstChildAggregation } from "../../../../components/src/react-table/features";
import { constructAggregationFn } from "@tanstack/react-table";

import {
    excludeInAttribution,
    getRowData,
    showModelIndex,
    aggregate2DArrayOfNumbers,
    getTopBottomInstrumentsFromSelectedRow,
    getTopBottomInstruments,
    isValidRow
} from "./functions";
import { SelectedRowType, PerformanceTreeProps, ProcessedData, PerformanceDataExcel } from "./interfaces";
import { accountReturnElement, benchmarkReturnElement, diffReturnElement } from "./components";
import { useProcessedData } from "./hooks";
import { ValuationDateCell } from "../../common/valuationDateStatus";

export const PerformanceTree = ({ client, data, endDate }: PerformanceTreeProps): React.ReactElement => {
    const [startDate] = useQueryState("startDate", client.firstTradeDate);
    const [, setQueryArgs] = useSearchParams();
    const [errors] = useState<string[]>([]);

    // this should set the data needed for the graph. TimeSeries for performance and array of TimeSeries for Benchmark
    const [performanceDataExcel, setPerformanceDataExcel] = useState<PerformanceDataExcel>({
        isNull: true,
        dateSeries: [],
        startDate: "1970-01-01",
        endDate: "2999-12-24",
        benchmarksById: {},
        accountSeriesById: {},
        accountPositionsById: {},
        modelSeriesById: {},
        useModelSeriesAsMain: true
    });

    // this should set the data needed for excel output
    const [selectedRow, setSelectedRow] = useState<SelectedRowType>({
        name: "",
        performance: null,
        values: [],
        instrumentPerformances: [],
        benchmarks: [],
        riskFree: null,
        startDate: startDate,
        endDate: endDate,
        dateSeries: [],
        positionFlag: []
    });

    const [hideZerosChecked, setHideZerosChecked] = useState(true);
    const [expandPositionsChecked, setExpandPositionsChecked] = useState(false);

    // The attribution charts and the graphs/tables block are expensive to mount and stall the
    // first paint by several seconds when switching to this tab. Defer them one tick so the toolbar and
    // portfolio table render immediately; the heavy charts then mount right after.
    const [showHeavyContent, setShowHeavyContent] = useState(false);
    useEffect(() => {
        const id = setTimeout(() => setShowHeavyContent(true), 0);
        return () => clearTimeout(id);
    }, []);

    const previousSelectedRow: SelectedRowType = usePrevious(selectedRow);
    const previousSelectedClient: Partial<Party> = usePrevious(client);

    // Signature of the payload the current `selectedRow` snapshot was built from. Rebuilding is keyed on
    // this value (not on the urql result's object identity) so a `cache-and-network` refetch that hands
    // back a value-identical payload with a fresh reference does NOT re-run setSelectedRow + setQueryArgs.
    // The old `data !== previousData` identity check did, and combined with the setQueryArgs navigation it
    // produced an update loop that exceeded React's update-depth limit (React error #185).
    const builtFromSignatureRef = React.useRef<string | null>(null);

    const sortBy = React.useMemo(
        () => [
            {
                id: "level1",
                desc: false
            },
            {
                id: "level2",
                desc: false
            },
            {
                id: "level3",
                desc: false
            },
            {
                id: "level4",
                desc: false
            },
            {
                id: "instrumentName",
                desc: false
            }
        ],
        []
    );

    const processedData: ProcessedData = useProcessedData({ endDate, startDate, data });

    const columns = React.useMemo(() => {
        if (!processedData) return [];
        const { dataWithLevels, dateSeries, benchmarksById, accountSeriesById, accountPositionsById, modelSeriesById, firstSelectedRow } =
            processedData;
        return [
            {
                Header: "",
                id: "metaData",
                columns: [
                    {
                        id: "rootId",
                        header: () => {
                            if (dataWithLevels && dataWithLevels.length > 0) {
                                return <Link to={`/parties/${client._id}/accounts/${dataWithLevels[0].accountId}`}>Client</Link>;
                            } else {
                                return <Link to={`/parties/${client._id}/accounts`}>Client</Link>;
                            }
                        },
                        accessorKey: "rootId",
                        size: 500,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            return (
                                <span
                                    className="hover__highlight__tree"
                                    onClick={() => {
                                        setSelectedRow(firstSelectedRow);
                                    }}
                                >
                                    {row.original.root}
                                </span>
                            );
                        },
                        getIsGrouped: () => true
                    },
                    {
                        header: "Client Description",
                        accessorKey: "root",
                        aggregationFn: firstChildAggregation
                    },
                    {
                        id: "level1Id",
                        header: () => {
                            if (dataWithLevels && dataWithLevels.length > 1) {
                                return <Link to={`/parties/${client._id}/accounts/${dataWithLevels[1].accountId}`}>Account L1</Link>;
                            } else {
                                return <Link to={`/parties/${client._id}/accounts`}>Account L1</Link>;
                            }
                        },
                        accessorKey: "level1Id",
                        cell: (cellProps) => {
                            const { row, cell } = cellProps;
                            return (
                                <span
                                    className="hover__highlight__tree"
                                    onClick={() => {
                                        const value = cell.getValue();
                                        const account = processedData.accountsById[value];

                                        const selectedRow = getRowData(
                                            row,
                                            processedData,
                                            row.original.level1,
                                            startDate,
                                            endDate,
                                            showModelIndex(account, processedData.maxAccountLevels, 1)
                                        );
                                        setSelectedRow(selectedRow);
                                    }}
                                >
                                    {row.original.level1}
                                </span>
                            );
                        }
                    },
                    {
                        id: "level2Id",
                        header: () => {
                            if (dataWithLevels && dataWithLevels.length > 2) {
                                return <Link to={`/parties/${client._id}/accounts/${dataWithLevels[2].accountId}`}>Account L2</Link>;
                            } else {
                                return <Link to={`/parties/${client._id}/accounts`}>Account L2</Link>;
                            }
                        },
                        accessorKey: "level2Id",
                        cell: (cellProps) => {
                            const { row, cell } = cellProps;
                            return (
                                <span
                                    className="hover__highlight__tree"
                                    onClick={() => {
                                        const value = cell.getValue();
                                        const account = processedData.accountsById[value];

                                        const selectedRow = getRowData(
                                            row,
                                            processedData,
                                            row.original.level2,
                                            startDate,
                                            endDate,
                                            showModelIndex(account, processedData.maxAccountLevels, 2)
                                        );
                                        setSelectedRow(selectedRow);
                                    }}
                                >
                                    {row.original.level2}
                                </span>
                            );
                        }
                    },
                    {
                        id: "level3Id",
                        header: () => {
                            if (dataWithLevels && dataWithLevels.length > 3) {
                                return <Link to={`/parties/${client._id}/accounts/${dataWithLevels[3].accountId}`}>Account L3</Link>;
                            } else {
                                return <Link to={`/parties/${client._id}/accounts`}>Account L3</Link>;
                            }
                        },
                        accessorKey: "level3Id",
                        cell: (cellProps) => {
                            const { row, cell } = cellProps;
                            return (
                                <span
                                    className="hover__highlight__tree"
                                    onClick={() => {
                                        const value = cell.getValue();
                                        const account = processedData.accountsById[value];

                                        const selectedRow = getRowData(
                                            row,
                                            processedData,
                                            row.original.level3,
                                            startDate,
                                            endDate,
                                            showModelIndex(account, processedData.maxAccountLevels, 3)
                                        );
                                        setSelectedRow(selectedRow);
                                    }}
                                >
                                    {row.original.level3}
                                </span>
                            );
                        }
                    },
                    {
                        id: "level4Id",
                        header: () => {
                            if (dataWithLevels && dataWithLevels.length > 4) {
                                return <Link to={`/parties/${client._id}/accounts/${dataWithLevels[4].accountId}`}>Account L4</Link>;
                            } else {
                                return <Link to={`/parties/${client._id}/accounts`}>Account L4</Link>;
                            }
                        },
                        accessorKey: "level4Id",
                        cell: (cellProps) => {
                            const { row, cell } = cellProps;
                            return (
                                <span
                                    className="hover__highlight__tree"
                                    onClick={() => {
                                        const value = cell.getValue();
                                        const account = processedData.accountsById[value];

                                        const selectedRow = getRowData(
                                            row,
                                            processedData,
                                            row.original.level4,
                                            startDate,
                                            endDate,
                                            showModelIndex(account, processedData.maxAccountLevels, 4)
                                        );
                                        setSelectedRow(selectedRow);
                                    }}
                                >
                                    {row.original.level4}
                                </span>
                            );
                        }
                    },
                    {
                        header: "Account L1 Description",
                        accessorKey: "level1",
                        aggregationFn: firstChildAggregation
                    },
                    {
                        header: "Account L2 Description",
                        accessorKey: "level2",
                        aggregationFn: firstChildAggregation
                    },
                    {
                        header: "Account L3 Description",
                        accessorKey: "level3",
                        aggregationFn: firstChildAggregation
                    },
                    {
                        header: "Account L4 Description",
                        accessorKey: "level4",
                        aggregationFn: firstChildAggregation
                    },
                    {
                        id: "instrumentName",
                        accessorFn: (row) => (row.instrument.longName ? row.instrument.longName : row.instrument.name),
                        header: "Instrument",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            const benchmark = row.getIsGrouped()
                                ? benchmarksById[row.groupingValue]
                                : benchmarksById[row.original.instrumentId]
                                  ? benchmarksById[row.original.instrumentId]
                                  : benchmarksById[row.original.accountId];
                            return (
                                <span>
                                    {cellProps.getValue() ? (
                                        <Link className="mr-1" to={`/instruments/${row.original.instrumentId}`}>
                                            #
                                        </Link>
                                    ) : null}
                                    <span
                                        className="hover__highlight__tree"
                                        onClick={() => {
                                            const selectedRow = getRowData(
                                                row,
                                                processedData,
                                                cellProps.getValue(),
                                                startDate,
                                                endDate,
                                                false,
                                                benchmark
                                            );
                                            setSelectedRow(selectedRow);
                                        }}
                                    >
                                        {cellProps.getValue()}
                                    </span>
                                </span>
                            );
                        }
                    },

                    {
                        header: "First trade date",
                        accessorKey: "firstTradeDate",
                        aggregationFn: constructAggregationFn({
                            aggregate: ({ rows }) => {
                                let minDateString = "2999-12-30";
                                for (const row of rows) {
                                    if (row.original.firstTradeDate < minDateString) {
                                        minDateString = row.original.firstTradeDate;
                                    }
                                }
                                return minDateString;
                            }
                        }),
                        getCanGroup: () => false
                    },
                    {
                        header: "Price date",
                        accessorKey: "valuationDate",
                        cell: (cellProps) => <ValuationDateCell value={cellProps.getValue()} referenceDate={endDate} />,
                        aggregatedCell: () => null,
                        getCanGroup: () => false,
                        size: 90
                    },
                    {
                        header: "Value (SEK)",
                        id: "valueSEK",
                        accessorFn: (row) => {
                            return row.values[row.values.length - 1];
                        },
                        aggregationFn: "sum",
                        cell: (cellProps) => <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>,
                        aggregatedCell: null, //use 'cell'
                        getCanGroup: () => false
                    },
                    {
                        header: "Values",
                        accessorKey: "values",
                        aggregationFn: constructAggregationFn({
                            aggregate: ({ rows, getValue }) => aggregate2DArrayOfNumbers(rows.map((row) => getValue(row) as number[]))
                        }),
                        cell: (_cellProps) => {
                            return null;
                        }
                    },
                    {
                        header: "Series",
                        accessorKey: "series",
                        cell: (_cellProps) => {
                            return null;
                        },
                        getCanGroup: () => false
                    }
                ]
            },
            {
                header: <div style={{ textAlign: "center" }}>{"Return MTD"}</div>,
                id: "returnMtd",
                columns: [
                    {
                        header: "Account",
                        id: "returnMtdAccount",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            const lastDate = new Date(endDate);
                            const startDate = new Date(lastDate.getFullYear(), lastDate.getMonth(), 0);
                            return accountReturnElement(row, dateSeries, startDate, lastDate, accountSeriesById);
                        },
                        aggregatedCell: null // the 'cell' is used instead default aggregatedCell
                    },
                    {
                        header: "Benchmark",
                        id: "returnMtdBenchmark",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            const lastDate = new Date(endDate);
                            const startDate = new Date(lastDate.getFullYear(), lastDate.getMonth(), 0);
                            return benchmarkReturnElement(
                                row,
                                dateSeries,
                                startDate,
                                lastDate,
                                benchmarksById,
                                accountPositionsById,
                                modelSeriesById
                            );
                        },
                        aggregatedCell: null // the 'cell' is used instead default aggregatedCell
                    },
                    {
                        header: "Difference",
                        id: "returnMtdDifference",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            const lastDate = new Date(endDate);
                            const startDate = new Date(lastDate.getFullYear(), lastDate.getMonth(), 0);
                            return diffReturnElement(
                                row,
                                dateSeries,
                                startDate,
                                lastDate,
                                benchmarksById,
                                accountSeriesById,
                                accountPositionsById,
                                modelSeriesById
                            );
                        },
                        aggregatedCell: null // the 'cell' is used instead default aggregatedCell
                    }
                ]
            },
            {
                header: <div style={{ textAlign: "center" }}>{"Return YTD"}</div>,
                id: "returnYtd",
                columns: [
                    {
                        header: "Account",
                        id: "returnYtdAccount",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            const lastDate = new Date(endDate);
                            // December month 11 in JavaScript!!!!
                            const startDate = new Date(lastDate.getFullYear() - 1, 11, 31);
                            return accountReturnElement(row, dateSeries, startDate, lastDate, accountSeriesById);
                        },
                        aggregatedCell: null // the 'cell' is used instead default aggregatedCell
                    },
                    {
                        header: "Benchmark",
                        id: "returnYtdBenchmark",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            const lastDate = new Date(endDate);
                            // December month 11 in JavaScript!!!!
                            const startDate = new Date(lastDate.getFullYear() - 1, 11, 31);
                            return benchmarkReturnElement(
                                row,
                                dateSeries,
                                startDate,
                                lastDate,
                                benchmarksById,
                                accountPositionsById,
                                modelSeriesById
                            );
                        },
                        aggregatedCell: null // the 'cell' is used instead default aggregatedCell
                    },
                    {
                        header: "Difference",
                        id: "returnYtdDifference",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            const lastDate = new Date(endDate);
                            // December month 11 in JavaScript!!!!
                            const startDate = new Date(lastDate.getFullYear() - 1, 11, 31);
                            return diffReturnElement(
                                row,
                                dateSeries,
                                startDate,
                                lastDate,
                                benchmarksById,
                                accountSeriesById,
                                accountPositionsById,
                                modelSeriesById
                            );
                        },
                        aggregatedCell: null // the 'cell' is used instead default aggregatedCell
                    }
                ]
            },
            {
                header: (
                    <div
                        style={{ textAlign: "center" }}
                        data-tooltip-id="ct-tooltip"
                        data-tooltip-place="bottom"
                        data-tooltip-position-strategy="fixed"
                        data-tooltip-content={
                            "\u2071: Compound Annual Growth Rate (CAGR) used for periods over one year. Linear return for one year and under."
                        }
                    >
                        {"Return chosen period (CAGR\u2071)"}
                    </div>
                ),
                id: "returnPer",

                columns: [
                    {
                        header: "Account",
                        id: "returnPerAccount",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            const lastDate = new Date(endDate);
                            const firstDate = new Date(startDate);
                            return accountReturnElement(row, dateSeries, firstDate, lastDate, accountSeriesById, true);
                        },
                        aggregatedCell: null // the 'cell' is used instead default aggregatedCell
                    },
                    {
                        header: "Benchmark",
                        id: "returnPerBenchmark",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            const lastDate = new Date(endDate);
                            const firstDate = new Date(startDate);
                            return benchmarkReturnElement(
                                row,
                                dateSeries,
                                firstDate,
                                lastDate,
                                benchmarksById,
                                accountPositionsById,
                                modelSeriesById,
                                true,
                                true
                            );
                        },
                        aggregatedCell: null // the 'cell' is used instead default aggregatedCell
                    },
                    {
                        header: "Difference",
                        id: "return1YDifference",
                        getCanGroup: () => false,
                        cell: (cellProps) => {
                            const { row } = cellProps;
                            const lastDate = new Date(endDate);
                            const firstDate = new Date(startDate);
                            return diffReturnElement(
                                row,
                                dateSeries,
                                firstDate,
                                lastDate,
                                benchmarksById,
                                accountSeriesById,
                                accountPositionsById,
                                modelSeriesById,
                                true,
                                true
                            );
                        },
                        aggregatedCell: null // the 'cell' is used instead default aggregatedCell
                    }
                ]
            }
        ];
    }, [client._id, startDate, endDate, processedData]);

    useEffect(() => {
        if (processedData) {
            const { firstSelectedRow, dateSeries, benchmarksById, accountSeriesById, accountPositionsById, modelSeriesById } =
                processedData;

            const startDateAttribution = !previousSelectedClient
                ? dateSeries[0]
                : previousSelectedClient && previousSelectedClient.firstTradeDate === startDate
                  ? dateSeries[0]
                  : startDate > dateSeries[0] || startDate < dateSeries[dateSeries.length - 1]
                    ? startDate
                    : dateSeries[0];

            // `selectedRow` holds deep snapshots of series/values taken from the payload it was built
            // from, so it must be rebuilt whenever a genuinely new performance payload arrives — both on
            // client switches and when a refetch delivers updated numbers for the same client. We detect
            // that with a value-based signature rather than the urql result's object identity, so
            // value-identical re-emits (which get a fresh reference under `cache-and-network`) do not
            // trigger a rebuild/navigation and cannot feed an update loop.
            const dates: string[] = (data && data.performance && data.performance.dates) || [];
            const values: number[] = (data && data.performance && data.performance.values) || [];
            const payloadSignature = [
                data && data.client ? data.client._id : "",
                endDate,
                dates.length,
                dates.length ? dates[dates.length - 1] : "",
                values.length ? values[values.length - 1] : ""
            ].join("|");
            const isNewPayload = builtFromSignatureRef.current !== payloadSignature;

            if (selectedRow.name === "" || isNewPayload) {
                builtFromSignatureRef.current = payloadSignature;
                setSelectedRow(firstSelectedRow);
                if (startDateAttribution !== startDate) {
                    setQueryArgs({ startDate: startDateAttribution, endDate });
                }
            } else if (previousSelectedRow && selectedRow.name !== previousSelectedRow.name) {
                if (startDateAttribution !== startDate) {
                    setQueryArgs({ startDate: startDateAttribution, endDate });
                }
            }
            // set data needed for
            setPerformanceDataExcel({
                isNull: false,
                dateSeries,
                startDate: startDateAttribution,
                endDate,
                benchmarksById,
                accountSeriesById,
                accountPositionsById,
                modelSeriesById,
                useModelSeriesAsMain: true
            });
        }
    }, [client._id, data, endDate, previousSelectedClient, previousSelectedRow, processedData, selectedRow, setQueryArgs, startDate]);

    if (errors && errors.length > 0) return <p>{errors}</p>;
    if (!processedData) return <div></div>;

    if (!client.firstTradeDate) return <p>Missing firstTradeDate</p>;

    if (startDate && endDate && endDate < startDate)
        return (
            <p>
                EndDate {endDate} cannot be earlier than startDate {startDate}
            </p>
        );

    let treeRootExist = false;
    if (data && "client" in data && "accounts" in data.client && data.client.topAccount) {
        treeRootExist = true;
    }

    // Extract data
    if (!treeRootExist) return <p>No account tree found</p>;
    const { maxAccountLevels, dataWithLevels } = processedData;

    const expandedPositions = expandPositionsChecked ? true : {};

    const hiddenColumns = ["series", "values", "root", "level1", "level2", "level3", "level4", "positionFlag"];

    const groupBy = ["rootId", "level1Id", "level2Id", "level3Id", "level4Id"];
    if (maxAccountLevels && maxAccountLevels.length < 5) {
        delete groupBy[4];
        hiddenColumns.push("level4Id");
        if (maxAccountLevels && maxAccountLevels.length < 4) {
            delete groupBy[3];
            hiddenColumns.push("level3Id");
            if (maxAccountLevels && maxAccountLevels.length < 3) {
                delete groupBy[2];
                hiddenColumns.push("level2Id");
                if (maxAccountLevels.length < 2) {
                    delete groupBy[1];
                    hiddenColumns.push("level1Id");
                }
            }
        }
    }

    let topBottomInstruments: string[] = [];
    if (selectedRow.name !== "" && selectedRow.instrumentPerformances.length) {
        topBottomInstruments = getTopBottomInstrumentsFromSelectedRow(selectedRow);
    } else {
        const defaultInstrumentsValues: Record<string, number> = {};
        for (const level of dataWithLevels) {
            if (
                level.instrument &&
                level.instrument.modelType &&
                !excludeInAttribution(level.instrument.modelType as unknown as InstrumentModelTypeEnum)
            ) {
                defaultInstrumentsValues[level.instrument.name] = level.series[level.series.length - 1];
            }
        }
        topBottomInstruments = getTopBottomInstruments(defaultInstrumentsValues);
    }

    const accountDescriptions: Record<string, number> = {};
    for (const row of dataWithLevels) {
        const account = processedData.accountsById[row.accountId];
        if (account && account.type === PartyAccountType.Physical && account.description) accountDescriptions[account.description] = 1;
    }

    const rowHasNonZeroValue = (values: number[]): boolean => {
        if (!values || values.length === 0) return false;
        return Math.abs(values[values.length - 1]) > 1e-6;
    };

    const filteredDataWithLevels = hideZerosChecked
        ? dataWithLevels.filter((row) => rowHasNonZeroValue(row.values))
        : dataWithLevels;

    const handleHideZerosChange = (e) => {
        setHideZerosChecked(e.target.checked);
    };

    const handleExpandPositionsChange = (e) => {
        setExpandPositionsChecked(e.target.checked);
    };

    return (
        <>
            <div className="sticky top-0 z-20 -mx-4 px-4 py-3 mb-1 bg-gray-100/90 backdrop-blur border-b border-slate-200 print-none">
                <div className="flex flex-wrap items-center gap-3">
                    <PerformancePeriodSelector firstAvailableStartDate={client.firstTradeDate} endDate={endDate} />
                    <label className="inline-flex items-center gap-2 select-none text-sm text-slate-700">
                        <input
                            type="checkbox"
                            checked={hideZerosChecked}
                            onChange={handleHideZerosChange}
                            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                        />
                        Hide zeros
                    </label>
                    <label className="inline-flex items-center gap-2 select-none text-sm text-slate-700">
                        <input
                            type="checkbox"
                            checked={expandPositionsChecked}
                            onChange={handleExpandPositionsChange}
                            className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                        />
                        Expand positions
                    </label>
                </div>
            </div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    <TableGrouper
                        key={`${client._id}-${endDate}-${hideZerosChecked}-${expandPositionsChecked}-${maxAccountLevels}-${groupBy.join()}`}
                        columns={columns}
                        data={filteredDataWithLevels}
                        expanded={expandedPositions}
                        groupBy={groupBy}
                        hiddenColumns={hiddenColumns}
                        compactMode={true}
                        captionColumn="instrumentName"
                        sortBy={sortBy}
                        showExcelButton={true}
                        performanceData={performanceDataExcel}
                    />
                </div>
            </div>
            <div>
                <div>
                    {showHeavyContent && isValidRow(selectedRow) ? (
                        <PerformanceGraphsTables selectedRow={{ ...selectedRow, endDate, startDate }} />
                    ) : null}
                </div>
            </div>
            <div className="flex flex-wrap -mx-2">
                {showHeavyContent && selectedRow.performance ? (
                    <div className="w-full md:w-1/2 px-2 mb-4 mt-4">
                        <AttributionGraph
                            data={{
                                client: {
                                    ...client,
                                    accounts: client.accounts.map((account) => ({
                                        ...account,
                                        weights: [],
                                        benchmarks: account.benchmarks.map((benchmark) => ({
                                            ...benchmark,
                                            mainBenchmark: false,
                                            showExternal: false,
                                            offset: 0,
                                            ...benchmark
                                        }))
                                    }))
                                },
                                performance: {
                                    dates: selectedRow.dateSeries,
                                    values: selectedRow.values,
                                    series: selectedRow.performance.__values,
                                    instrumentPerformances: selectedRow.instrumentPerformances as any
                                },
                                topBottomInstruments: topBottomInstruments,
                                accountDescriptions: Object.keys(accountDescriptions).sort(),
                                accountsById: processedData.accountsById
                            }}
                            selector={processedData ? processedData.attributionSelector : AttributionSelectorEnum.Instruments}
                            plotType={PlotTypeEnum.Series}
                            style={{ height: "50vh" }}
                            client={client}
                            showForm={false}
                        />
                    </div>
                ) : null}
                {showHeavyContent && selectedRow.performance ? (
                    <div className="w-full md:w-1/2 px-2 mb-4 mt-4">
                        <AttributionGraph
                            data={{
                                client: {
                                    ...client,
                                    accounts: client.accounts.map((account) => ({
                                        ...account,
                                        weights: [],
                                        benchmarks: account.benchmarks.map((benchmark) => ({
                                            ...benchmark,
                                            mainBenchmark: false,
                                            showExternal: false,
                                            offset: 0,
                                            ...benchmark
                                        }))
                                    }))
                                },
                                performance: {
                                    dates: selectedRow.dateSeries,
                                    values: selectedRow.values,
                                    series: selectedRow.performance.__values,
                                    instrumentPerformances: selectedRow.instrumentPerformances as any
                                },
                                topBottomInstruments: topBottomInstruments,
                                accountDescriptions: Object.keys(accountDescriptions).sort(),
                                accountsById: processedData.accountsById
                            }}
                            selector={processedData ? processedData.attributionSelector : AttributionSelectorEnum.Instruments}
                            plotType={PlotTypeEnum.Waterfall}
                            style={{ height: "50vh" }}
                            client={client}
                            showForm={false}
                            showXlsxButton={false}
                        />
                    </div>
                ) : null}
            </div>
            <PerformanceCagrTooltip />
        </>
    );
};
